import { useState, useCallback, useRef } from "react";
import { apiV1 } from "api-v2";
import { useCurrentCompanyId } from "hooks/auth";
import type { PreflightResponse } from "../../../types/userInvite";

interface UseInvitePreflightReturn {
  result: PreflightResponse | null;
  isLoading: boolean;
  error: string | null;
  checkEmail: (email: string) => Promise<void>;
  clearResult: () => void;
}

export const useInvitePreflight = (): UseInvitePreflightReturn => {
  const companyId = useCurrentCompanyId();
  const [result, setResult] = useState<PreflightResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastEmailRef = useRef<string>("");

  const checkEmail = useCallback(async (email: string) => {
    if (!companyId || !email) {
      return;
    }
    lastEmailRef.current = email;
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiV1.post<PreflightResponse>(
        `companies/${companyId}/users/invite/preflight`,
        { email }
      );
      if (lastEmailRef.current === email) {
        setResult(response.data);
      }
    } catch {
      if (lastEmailRef.current === email) {
        setError("Failed to check email");
        setResult(null);
      }
    } finally {
      if (lastEmailRef.current === email) {
        setIsLoading(false);
      }
    }
  }, [companyId]);

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
    lastEmailRef.current = "";
  }, []);

  return { result, isLoading, error, checkEmail, clearResult };
};
